import { resolveRecruitmentPermissions } from "../../services/recruitmentPermissions";
// WeeklyRecruitmentEmailPreview.jsx
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { createPortal } from "react-dom";
import { generateStyledEmailTemplate } from "./generateStyledEmailTemplate";
import "../../../../css/Recruitment/WeeklyRecruitmentEmailPreview.css";
import formatUKPhone from "../../../AdviserSupport/ContactList/HelperFunctions/formatUKPhone";

// ---------- helpers ----------
const parseD = (d) => {
  if (!d) return null;
  if (d?.toDate) return dayjs(d.toDate());
  if (dayjs(d).isValid()) return dayjs(d);
  return null;
};
const fmt = (d) => parseD(d)?.format("DD/MM/YYYY, HH:mm:ss") || "";
const fmtDate = (d) => parseD(d)?.format("DD/MM/YYYY") || "";

const isSubmitted = (a) => !!a?.recruitmentApplicationSubmittedAt;
const isFinalised = (a) =>
  a?.isAppointed === true || a?.notProceedingWith === true;
const inPipeline = (a) => isSubmitted(a) && !isFinalised(a);

const appointedInLastWeek = (a) => {
  if (!a?.isAppointed) return false;
  const when = parseD(a?.appointedAt);
  if (!when) return false;
  return when.isAfter(dayjs().subtract(6, "day").startOf("day"));
};

// 1) Reusable inline styles
const TH_STYLE =
  "background:#081e56;color:#ffffff;font:12px/1.2 Arial,Helvetica,sans-serif;padding:10px;border:1px solid #d0d4dc;text-align:left;vertical-align:top;";
const TD_STYLE =
  "padding:10px;border:1px solid #d0d4dc;text-align:left;vertical-align:middle;" +
  "font:12px/1.55 Arial,Helvetica,sans-serif;color:#202333;" +
  "white-space:normal;word-break:keep-all;overflow-wrap:break-word;";

const TD_INDEX_STYLE =
  "padding:10px;border:1px solid #d0d4dc;text-align:left;vertical-align:middle;" +
  "font:12px/1.55 Arial,Helvetica,sans-serif;color:#202333;white-space:nowrap;width:10px;";

const row = (cells, index = 0) => {
  const bg = index % 2 === 0 ? "#ffffff" : "#f4f4f9"; // light blue stripe

  return `<tr style="background:${bg};">${cells
    .map((c, i) =>
      i === 0
        ? `<td style="${TD_INDEX_STYLE}">${c ?? "-"}</td>`
        : `<td style="${TD_STYLE}">${c ?? "-"}</td>`
    )
    .join("")}</tr>`;
};

// const row = (cells) =>
//   `<tr>${cells.map((c) => `<td>${c ?? "-"}</td>`).join("")}</tr>`;

const formatArTsName = (adviser) => {
  const update = adviser.recruitmentUpdates?.[0] || {};
  const isAR = adviser.isAR ?? update.isAR;
  const joiningAs = isAR === true ? "AR" : isAR === false ? "TS" : "";
  const name = String(update.arTsName || "").trim();
  return joiningAs && name ? `${joiningAs}: ${name}` : joiningAs || name;
};

const buildWeeklyDigestInnerHtml = (apps = []) => {
  const pipeline = apps.filter(inPipeline);
  const appointed = apps.filter(appointedInLastWeek);

  const pipelineRows = pipeline.length
    ? pipeline
        .map((a, i) =>
          row(
            [
              // i + 1,
              `${a.recruitmentUpdates?.[0]?.applicantName || a.name || ""}
            `,
              a.recruitmentUpdates?.[0]?.source || "",
              formatArTsName(a),
              fmtDate(a.recruitmentUpdates?.[0]?.lastContacted),
              // use your correct field name here; previously "anticipatedAppointmentDate"
              a.recruitmentUpdates?.[0]?.anticipatedAppointmentDate ||
                a.recruitmentUpdates?.[0]?.appointmentData ||
                "",
              resolveRecruitmentPermissions(a, a.recruitmentUpdates?.[0]).join(", "),
              a.recruitmentUpdates?.[0]?.description ||
                a.recruitmentUpdates?.[0]?.update ||
                "No updates available.",
            ],
            i
          )
        )
        .join("")
    : row(["-", "No applications in pipeline this week.", "", "", "", "", "","",]);

  const appointedRows = appointed.length
    ? appointed
        .map((a, i) =>
          row([
            `${a.recruitmentUpdates?.[0]?.applicantName || a.name || "-"}`,
            fmtDate(a.appointedAt),
          ])
        )
        .join("")
    : row(["No advisers were appointed in the last 7 days.", ""]);

  const appointedSection = appointed.length
    ? `
  <p style="margin:50px 0 0px 0px; font:12px/1.2 sans-serif; color:#000000;">
    Appointed (last 7 days)
  </p>

  <!-- Appointed table -->
  <table role="presentation" cellpadding="0" cellspacing="0" border="1"
    style="width:100%;border-collapse:collapse;margin:12px 0 0 0;border:1px solid #d0d4dc;">
    <colgroup>
      <col style="width:65%;">
      <col style="width:35%;">
    </colgroup>
    <thead>
      <tr>
        <th style="${TH_STYLE}">Name</th>
        <th style="${TH_STYLE}">Appointed On</th>
      </tr>
    </thead>
    <tbody style="font:12px/1.2 sans-serif;">
      ${appointedRows}
    </tbody>
  </table>
  `
    : "";

  return `
  <p style="margin:0 0 12px 0;font:14px/1.2 sans-serif">
    Please find below an update on our new recruits and where we currently are with their recruitment.
  </p>

  <!-- Pipeline table -->
<table role="presentation" cellpadding="0" cellspacing="0" border="1"
  style="width:100%;border-collapse:collapse;margin:12px 0 0 0;border:1px solid #d0d4dc;">
     <colgroup>
      <col style="width:10%;">
      <col style="width:10%;">
      <col style="width:14%;">
      <col style="width:10%;">
      <col style="width:15%;">
      <col style="width:13%;">
      <col style="width:30%;"> <!-- Notes gets the most space -->
    </colgroup>
    <thead>
    <tr>
      <th style="${TH_STYLE}">Name</th>
      <th style="${TH_STYLE}">Source</th>
      <th style="${TH_STYLE}">AR/TS Name</th>
      <th style="${TH_STYLE}">Last Contacted</th>
      <th style="${TH_STYLE}">Anticipated Appointment Date</th>
      <th style="${TH_STYLE}">Permission</th>
      <th style="${TH_STYLE}">Notes</th>
    </tr>
  </thead>
  <tbody>
    ${pipelineRows}
  </tbody>
</table>


${appointedSection}

  <p style="margin:14px 0 0 0; font:14px/1.2 sans-serif;">
    If you have any queries, please contact
    <a href="mailto:andre.oleary@rosemountfs.co.uk" style="color:#081e56;text-decoration:none;">
      André O'Leary
    </a>.
  </p>
`;
};

const isFullDoc = (html = "") => /<html[\s\S]*?>/i.test(html);

// ---------- component ----------
const WeeklyRecruitmentEmailPreview = ({
  apps = [],
  onClose,
  sendFn,
  onSent,
}) => {
  const [sending, setSending] = useState(false);
  const subject = `Recruitment Update`;
  // const subject = `Recruitment Update - ${dayjs().format("DD MMM YYYY")}`;

  const inner = useMemo(() => buildWeeklyDigestInnerHtml(apps), [apps]);
  const srcDoc = useMemo(
    () => (isFullDoc(inner) ? inner : generateStyledEmailTemplate(inner)),
    [inner]
  );

  const endpoint = process.env.REACT_APP_SERVER_URL;

  // useEffect(() => {
  //   console.log(endpoint);
  // }, []);

  const doSend = async () => {
    setSending(true);
    try {
      const payload = { emailPreviews: [{ subject, body: inner }] }; // recipients fetched on backend
      if (sendFn) await sendFn(`${endpoint}send-recruitment-updates`, payload);
      else {
        await fetch(`${endpoint}send-recruitment-updates`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      onSent?.();
      onClose?.();
    } catch (e) {
      // console.error("Failed to send digest:", e);
      alert("Failed to send digest. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const modal = (
    <div className="AEP-Overlay" role="dialog" aria-modal="true">
      <div className="AEP-Card">
        {/* <button
          className="AEP-Close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          ×
        </button> */}

        <div className="AEP-Meta">
          <div>
            <strong>Subject:</strong> {subject}
          </div>
        </div>

        <iframe
          title="Weekly digest preview"
          className="AEP-Iframe"
          srcDoc={srcDoc}
        />

        <div className="AEP-Footer">
          <button className="AEP-Btn Secondary" onClick={onClose} type="button">
            Close
          </button>
          {/* <button
            className="AEP-Btn Primary"
            onClick={doSend}
            disabled={sending}
            type="button"
          >
            {sending ? "Sending..." : "Send"}
          </button> */}
        </div>
      </div>
    </div>
  );

  // Render via portal so the overlay always covers the page
  return createPortal(modal, document.body);
};

WeeklyRecruitmentEmailPreview.propTypes = {
  apps: PropTypes.array.isRequired,
  endpoint: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  sendFn: PropTypes.func,
  onSent: PropTypes.func,
};

export default WeeklyRecruitmentEmailPreview;
